'use client';

import { c as _c } from "react/compiler-runtime";
import type { db } from '@funkey/api/src';
import type { BizzPermission, PartnerPermission } from '@funkey/shared/permissions';
import { ASSERT_UNREACHABLE } from '@funkey/shared/errors';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useRouter } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import type { LinkProps } from 'next/link';
import Link from 'next/link';
import type { UrlObject } from 'url';
import { GraphQLClientProvider } from '~/modules/api';
import { I18N_CONF } from '@funkey/locales/config';
import { useI18n } from '~/features/i18n';
import { ComputedOrImmediate } from '@funkey/shared';
import * as Brand from 'effect/Brand';
export declare namespace usePortalCtx {
  export type BizzCtx = {
    portal: 'BIZZ';
    baseURLPath: `/${string}`;
    bizz: Pick<typeof db.businesses.$inferSelect,
    // Add more fields if needed.
    // NOTE: only JSON serialisable fields can be added.
    // If we want dates etc we should look into using superjson, then we shouldn't have to pick every field
    'id' | 'name' | 'tenantId' | 'slug' | 'publicPages' | 'vat' | 'vatInclusive' | 'vatRate' | 'brandingColors' | 'helpCenterUrl' | 'disableCatering' | 'disableServices' | 'disableCoordination' | 'activityFilters' | 'enableCreditWallet' | 'settings' | 'automaticallyAddNewActivitiesToSelection' | 'activityFiltersLastReviewedAt' | 'createdAt'> & {
      logo: typeof db.media.$inferSelect.path | null;
      excludedActivityIDs: number[];
    } & Pick<typeof db.tenants.$inferSelect, 'enableSaml' | 'enableCredentials'>;

    /** The permissions the current browser session has */
    permissions: BizzPermission[];
  };
  export type PartnerCtx = {
    portal: 'PARTNER';
    baseURLPath: `/${string}`;
    partner: Pick<typeof db.partners.$inferSelect,
    // Add more fields if needed.
    // NOTE: only JSON serialisable fields can be added.
    // If we want dates etc we should look into using superjson, then we shouldn't have to pick every field
    'id' | 'name' | 'tenantId' | 'slug' | 'countryCode' | 'language' | 'supportedCountryCode' | 'type' | 'zohoId' | 'isPreferredActivityPartner' | 'status'>;

    /** The permissions the current browser session has */
    permissions: PartnerPermission[];

    /** Whether the current session belongs to a Funkey superadmin (used to bypass partner-only gates for testing). */
    isSuperAdmin: boolean;
  };
  export type CtxType =
  /** The public website drops the locale prefix for the country-default locale, so its base
   * path is empty there. (FUN-5037) */
  {
    portal: 'WEB';
    baseURLPath: '' | `/${string}`;
  } | PartnerCtx | {
    portal: 'ADMIN';
    baseURLPath: `/${string}`;
  } | BizzCtx;
  export type Portal = CtxType['portal'];
  export type getByPortal<T extends Portal> = Extract<CtxType, {
    portal: T;
  }>;
}
const CTX = createContext<usePortalCtx.CtxType>(null!);
export function usePortalCtx(portal) {
  const ctx = useContext(CTX);
  if (portal && ctx.portal !== portal) {
    throw new Error(`We're not in the requested portal "${portal}". Current portal: "${ctx.portal}"`);
  }
  return ctx as usePortalCtx.getByPortal<T>;
}
export const PortalContextProvider: React.FC<React.PropsWithChildren<usePortalCtx.CtxType>> = t0 => {
  const $ = _c(29);
  let children;
  let value;
  if ($[0] !== t0) {
    ({
      children,
      ...value
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = value;
  } else {
    children = $[1];
    value = $[2];
  }
  const posthog = usePostHog();
  let t1;
  if ($[3] !== posthog || $[4] !== value) {
    t1 = () => {
      bb2: switch (value.portal) {
        case "WEB":
        case "ADMIN":
          {
            posthog.group("portal", value.portal, {
              type: value.portal
            });
            break bb2;
          }
        case "PARTNER":
          {
            posthog.group("portal", `${value.portal}:${value.partner.id}`, {
              id: value.partner.id,
              type: value.portal,
              name: value.partner.name,
              slug: value.partner.slug,
              tenantId: value.partner.tenantId
            });
            break bb2;
          }
        case "BIZZ":
          {
            posthog.group("portal", `${value.portal}:${value.bizz.id}`, {
              id: value.bizz.id,
              type: value.portal,
              name: value.bizz.name,
              slug: value.bizz.slug,
              tenantId: value.bizz.tenantId
            });
            break bb2;
          }
        default:
          {
            ASSERT_UNREACHABLE(value);
          }
      }
    };
    $[3] = posthog;
    $[4] = value;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== value) {
    t2 = [value];
    $[6] = value;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  bb0: switch (value.portal) {
    case "PARTNER":
      {
        let t4;
        if ($[8] !== value.partner.id) {
          t4 = value.partner.id.toString();
          $[8] = value.partner.id;
          $[9] = t4;
        } else {
          t4 = $[9];
        }
        let t5;
        if ($[10] !== value.partner.tenantId) {
          t5 = value.partner.tenantId.toString();
          $[10] = value.partner.tenantId;
          $[11] = t5;
        } else {
          t5 = $[11];
        }
        let t6;
        if ($[12] !== t4 || $[13] !== t5) {
          t6 = {
            "x-partner-id": t4,
            "x-tenant-id": t5
          };
          $[12] = t4;
          $[13] = t5;
          $[14] = t6;
        } else {
          t6 = $[14];
        }
        t3 = t6;
        break bb0;
      }
    case "BIZZ":
      {
        let t4;
        if ($[15] !== value.bizz.id) {
          t4 = value.bizz.id.toString();
          $[15] = value.bizz.id;
          $[16] = t4;
        } else {
          t4 = $[16];
        }
        let t5;
        if ($[17] !== t4) {
          t5 = {
            "x-bizz-id": t4
          };
          $[17] = t4;
          $[18] = t5;
        } else {
          t5 = $[18];
        }
        t3 = t5;
        break bb0;
      }
    case "ADMIN":
    case "WEB":
    default:
      {
        let t4;
        if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = {};
          $[19] = t4;
        } else {
          t4 = $[19];
        }
        t3 = t4;
      }
  }
  let t4;
  if ($[20] !== t3 || $[21] !== value.portal) {
    t4 = {
      ...t3,
      "x-portal": value.portal
    };
    $[20] = t3;
    $[21] = value.portal;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== children || $[24] !== t4) {
    t5 = <GraphQLClientProvider headers={t4}>{children}</GraphQLClientProvider>;
    $[23] = children;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== t5 || $[27] !== value) {
    t6 = <CTX.Provider value={value}>{t5}</CTX.Provider>;
    $[26] = t5;
    $[27] = value;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
};
export function usePortalRouter() {
  const $ = _c(5);
  const portal = useContext(CTX);
  const router = useRouter();
  const i18n = useI18n();
  let t0;
  if ($[0] !== portal || $[1] !== router) {
    let t1;
    if ($[3] !== portal) {
      t1 = path => relativeHREF(portal, path);
      $[3] = portal;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = {
      ...router,
      portal,
      push(href, options) {
        return router.push(relativeHREF(portal, href), options);
      },
      replace(href_0, options_0) {
        return router.replace(relativeHREF(portal, href_0), options_0);
      },
      prefetch(href_1, options_1) {
        return router.prefetch(relativeHREF(portal, href_1), options_1);
      },
      relativePath: t1
    };
    $[0] = portal;
    $[1] = router;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export declare namespace PortalLink {
  export type Props = React.PropsWithChildren<Omit<LinkProps & React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    className?: string;
    /** A relative path to the `baseURLPath` of the portal the user is currently in. */
    relativeHref: string | Omit<UrlObject, 'href'>;
  }>;
}
export const PortalLink: React.FC<PortalLink.Props> = t0 => {
  const $ = _c(9);
  let props;
  let relativeHref;
  if ($[0] !== t0) {
    ({
      relativeHref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = relativeHref;
  } else {
    props = $[1];
    relativeHref = $[2];
  }
  const portal = usePortalCtx();
  let t1;
  if ($[3] !== portal || $[4] !== relativeHref) {
    t1 = typeof relativeHref === "string" ? relativeHREF(portal, relativeHref) : {
      ...relativeHref,
      pathname: relativeHref.pathname ? relativeHREF(portal, relativeHref.pathname) : relativeHref.pathname
    };
    $[3] = portal;
    $[4] = relativeHref;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <Link {...props} href={t1} />;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};

/**
 * Provides portal-aware href helpers so callers don't need to branch on portal type.
 *
 * Usage:
 * ```ts
 * const hrefs = usePortalRouter().hrefs;
 * hrefs.activity({ slug: 'paintball', id: 42 })          // activity detail
 * hrefs.activity({ slug: 'paintball', id: 42, participants: 20 })
 * hrefs.browse()                                          // activity browsing page
 * ```
 */
export function usePortalHREFs() {
  const $ = _c(3);
  const i18n = useI18n();
  const portal = usePortalCtx();
  let t0;
  if ($[0] !== i18n.locale || $[1] !== portal) {
    t0 = {
      PDP(opts) {
        const params = new URLSearchParams();
        params.set("id", String(opts.id));
        if (opts.participants) {
          params.set("participants", String(opts.participants));
        }
        if (opts.boostAdId) {
          params.set("boostAdId", String(opts.boostAdId));
        }
        const qs = params.toString();
        if (portal.portal === "BIZZ") {
          return relativeHREF(portal, `/booking/${opts.slug}?${qs}`);
        }
        return `${I18N_CONF.getTranslatedUrl("activities", {
          locale: i18n.locale,
          slug: opts.slug
        })}?${qs}`;
      },
      PLP() {
        return portal.portal === "BIZZ" ? relativeHREF(portal, "/booking") : I18N_CONF.getTranslatedUrl("teambuildings", {
          locale: i18n.locale
        });
      },
      relative(path) {
        return relativeHREF(portal, path);
      }
    };
    $[0] = i18n.locale;
    $[1] = portal;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export type Portal_RelativeHREF = Brand.Branded<`/${string}`, 'PortalHREF'>;
export const Portal_RelativeHREF = Brand.refined<Portal_RelativeHREF>(path => path.startsWith('/'), path => Brand.error(`"${path}" is not a valid Portal_RelativeHREF`));
export function usePortalHREF_relative(relativePath) {
  const $ = _c(3);
  const portal = usePortalCtx();
  let t0;
  if ($[0] !== portal || $[1] !== relativePath) {
    t0 = relativeHREF(portal, ComputedOrImmediate.get(relativePath, portal));
    $[0] = portal;
    $[1] = relativePath;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
function relativeHREF(portal: usePortalCtx.CtxType, href: string): `/${string}` {
  const href_normalized = href.startsWith('/') ? href : `/${href}`;
  const base_normalized = portal.baseURLPath.endsWith(`/`) ? portal.baseURLPath.slice(0, portal.baseURLPath.length - 1) : portal.baseURLPath;
  return `${base_normalized}${href_normalized}` as `/${string}`;
}